import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ApprovalRequestState } from '../../../types/ApprovalRequestState';
import { Button } from '../../cdl/Button/Button';
import { Text } from '../../cdl/Text/Text';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { translate } from '../../common/helpers/translate.helper';
import { OverlayHeader } from '../../common/Overlay/OverlayHeader';
import { ReadOnlyProductTable } from '../../common/ReadOnlyProductTable/ReadOnlyProductTable';
import { Box } from '../../common/ui/Box';
import { OfferModel } from '../model/OfferModel';
import { CustomerApprovalDataSheet } from '../OfferCoreData/CustomerApprovalDataSheet';

import { ApprovalRequestContentContainer } from './ApprovalRequestContentContainer';
import { ApprovePopup } from './ApprovePopup';
import { DeclinePopup } from './DeclinePopup';
import { ApprovalRequestsDropdown } from './Dropdown/ApprovalRequestsDropdown';
import { PendingStatusBar } from './PendingStatusBar';

const ApprovalRequestPopups = {
    APPROVE: 'APPROVE',
    DECLINE: 'DECLINE',
} as const;

interface PendingStateProps {
    offer: OfferModel;
    approvalRequests: any[];
    onApprovalRequestApproved: (message?: string) => void;
    onApprovalRequestDeclined: (message?: string) => void;
    updatingApprovalRequestState: ApprovalRequestState;
    shouldDisplayBackButton: boolean;
}

export const PendingState = ({
    offer,
    approvalRequests,
    onApprovalRequestDeclined,
    onApprovalRequestApproved,
    updatingApprovalRequestState,
    shouldDisplayBackButton,
}: PendingStateProps) => {
    const [openPopup, setOpenPopup] = useState<keyof typeof ApprovalRequestPopups | null>(null);
    const navigate = useNavigate();

    const handleClose = shouldDisplayBackButton ? () => navigate({ to: '/dashboard' }) : undefined;

    return (
        <OverlayHeader
            title={
                <Box display="flex" gap={16}>
                    <Text variant="title">{translate('approvalRequest.header.title')}</Text>
                    <Text variant="title" color="foreground.subtle">
                        {offer.offerNumber}
                    </Text>
                </Box>
            }
            onClose={handleClose}
            trailingVisual={
                <Box display="flex" alignItems="center" gap={16}>
                    <ApprovalRequestsDropdown approvalRequests={approvalRequests} />
                    <ButtonGroup>
                        <Button onClick={() => setOpenPopup(ApprovalRequestPopups.DECLINE)}>
                            {translate('approvalRequest.buttons.decline')}
                        </Button>
                        <Button
                            emphasis="high"
                            onClick={() => setOpenPopup(ApprovalRequestPopups.APPROVE)}
                            isLoading={updatingApprovalRequestState === ApprovalRequestState.APPROVED}
                        >
                            {translate('approvalRequest.buttons.approve')}
                        </Button>
                    </ButtonGroup>
                </Box>
            }
        >
            <ApprovalRequestContentContainer>
                <PendingStatusBar approvalRequest={approvalRequests[0]} />
                <CustomerApprovalDataSheet offer={offer} />
                <ReadOnlyProductTable
                    items={offer.items}
                    sampleKits={offer.samplekits}
                    surcharges={offer.surcharges}
                    totals={{ volume: offer.volume, price: offer.ppl, total: offer.total }}
                    showPrices
                />
            </ApprovalRequestContentContainer>

            <DeclinePopup
                open={openPopup === ApprovalRequestPopups.DECLINE}
                onDismiss={() => setOpenPopup(null)}
                onSubmit={onApprovalRequestDeclined}
                requestInProgress={updatingApprovalRequestState === ApprovalRequestState.DECLINED}
            />

            <ApprovePopup
                open={openPopup === ApprovalRequestPopups.APPROVE}
                onDismiss={() => setOpenPopup(null)}
                onSubmit={onApprovalRequestApproved}
                requestInProgress={updatingApprovalRequestState === ApprovalRequestState.APPROVED}
            />
        </OverlayHeader>
    );
};
